import {
    defineComponent,
    defineAsyncComponent,
    ref,
    watch,
    createApp,
} from 'vue'
import { BlockModule } from '../common'
import { scrollToEl } from '@/lib/scrolling'
import { useIntersectionObserver } from '@vueuse/core'
import { tailwindBreakpoints } from '../tailwind'

new BlockModule({
    selector: '.block-product-plans',
    async setupBlock(el, onTeardown) {
        const swiperContainer = el.querySelector('.swiper') as HTMLElement
        if (!swiperContainer) return

        const [{ Swiper }, { Scrollbar }] = await Promise.all([
            import('swiper'),
            import('swiper/modules'),
            // @ts-ignore
            import('swiper/css'),
            // @ts-ignore
            import('swiper/css/scrollbar'),
        ])

        const urlParams = new URLSearchParams(window.location.search)
        const planParam = urlParams.get('plan')

        const slides = Array.from(
            swiperContainer.querySelectorAll('.swiper-slide'),
        ) as HTMLElement[]

        const targetIndex = slides.findIndex(
            (slide) => slide.getAttribute('data-plan-id') === planParam,
        )

        if (slides.length <= 1) return

        const productPlansCardBlock = el.querySelector('.product-plans-card-block')

        if (productPlansCardBlock) {
            const productPlansVueAppComponent = defineComponent({
                name: 'product-plans-card-block',
                components: {
                    CollapsibleRoot: defineAsyncComponent(() =>
                        import('radix-vue').then((x) => x.CollapsibleRoot),
                    ),
                    CollapsibleContent: defineAsyncComponent(() =>
                        import('radix-vue').then((x) => x.CollapsibleContent),
                    ),
                    CollapsibleTrigger: defineAsyncComponent(() =>
                        import('radix-vue').then((x) => x.CollapsibleTrigger),
                    ),
                    SocialShareDialog: defineAsyncComponent(
                        () => import('@/components/website/social-share/SocialShareDialog.vue'),
                    ),
                },
                props: {
                    currentActivePlan: String,
                },
                setup() {
                    const planCards = ref<HTMLElement[]>([])
                    const isPlanViewMore = ref(false)

                    watch(isPlanViewMore, (val) => {
                        const plansEl = document.getElementById('plans') as HTMLElement
                        scrollToEl(plansEl)
                    })

                    const lastCardRef = ref<HTMLElement | null>(null)
                    const isLastCardVisible = ref(false)

                    useIntersectionObserver(
                        lastCardRef,
                        ([{ isIntersecting }]) => {
                            isLastCardVisible.value = isIntersecting
                        },
                        { 
                            root: swiperContainer,
                            threshold: 0.8 },
                    )

                    return {
                        planCards,
                        isPlanViewMore,
                        lastCardRef,
                        isLastCardVisible,
                    }
                },
            })

            const productPlansVueApp = createApp(productPlansVueAppComponent)
            productPlansVueApp.mount(productPlansCardBlock)

            onTeardown(() => {
                productPlansVueApp.unmount()
            })
        }

        const swiper = new Swiper(swiperContainer, {
            modules: [Scrollbar],
            slidesPerView: 'auto',
            spaceBetween: 16,
            scrollbar: {
                el: '.swiper-scrollbar',
                draggable: true,
            },
            breakpoints: {
                [tailwindBreakpoints.md]: {
                    spaceBetween: 20,
                }
            }
        })

        if (targetIndex !== -1) {
            swiper.slideTo(targetIndex, 500, true)
        }

        onTeardown(() => {
            swiper.destroy()
        })
    },
})
